/**
 * The Integration Program — 21-Day Journey
 * 
 * Purchase gate with discount code → Day-by-day journey
 * Each day: Video → Integration → Reflection → Night Integration
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Play, Pause, Lock, Check, ChevronRight,
  BookOpen, Moon, Sparkles, Tag, Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeIn = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const slideUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};
const stagger = { animate: { transition: { staggerChildren: 0.08 } } };

// ─── Purchase Gate ───────────────────────────────
const PurchaseGate = ({ overview, onAccessGranted }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [codeValid, setCodeValid] = useState(null);

  const token = localStorage.getItem("reclaim_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const validateCode = async () => {
    if (!code.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/integration-program/validate-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setCodeValid(data);
        setError("");
      } else {
        setCodeValid(null);
        setError("Invalid code");
      }
    } catch {
      setError("Error validating code");
    }
  };

  const handlePurchase = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/integration-program/purchase`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          origin_url: window.location.origin,
          discount_code: code.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.has_access || data.already_purchased) {
        onAccessGranted();
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Something went wrong");
      }
    } catch {
      setError("Purchase failed");
    } finally {
      setLoading(false);
    }
  };

  const finalPrice = codeValid
    ? overview.price * (1 - codeValid.discount_percent / 100)
    : overview.price;

  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center px-8"
      style={{ background: "#0a0d12" }}
      {...fadeIn}
      transition={{ duration: 1 }}
      data-testid="purchase-gate"
    >
      <motion.div
        className="absolute w-[400px] h-[250px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(99,102,241,0.04) 0%, transparent 60%)", filter: "blur(60px)" }}
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: 999, repeatType: "loop" }}
      />

      <motion.div className="text-center relative z-10 max-w-md w-full" variants={stagger} initial="initial" animate="animate">
        <motion.p variants={slideUp} className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: "rgba(165,180,252,0.5)" }}>
          Reclaim
        </motion.p>
        <motion.h1 variants={slideUp} className="text-3xl mb-3" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.9)" }}>
          The Integration Program
        </motion.h1>
        <motion.p variants={slideUp} className="text-sm italic mb-10" style={{ fontFamily: "'Spectral', serif", color: "rgba(165,180,252,0.35)" }}>
          A 21-day guided integration journey
        </motion.p>

        {/* Discount code */}
        <motion.div variants={slideUp} className="mb-6">
          <div className="flex gap-2 mb-2">
            <div className="flex-1 relative">
              <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "rgba(255,255,255,0.15)" }} />
              <input
                value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeValid(null); setError(""); }}
                onBlur={validateCode}
                onKeyDown={(e) => e.key === "Enter" && validateCode()}
                placeholder="Discount code"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-indigo-400/30"
                data-testid="discount-code-input"
              />
            </div>
          </div>
          {codeValid && (
            <p className="text-xs text-green-400/70 flex items-center gap-1">
              <Check size={12} /> {codeValid.discount_percent}% discount applied
            </p>
          )}
          {error && <p className="text-xs text-red-400/70">{error}</p>}
        </motion.div>

        {/* Price */}
        <motion.div variants={slideUp} className="mb-8">
          {codeValid && codeValid.discount_percent >= 100 ? (
            <p className="text-lg" style={{ color: "rgba(134,239,172,0.7)" }}>Free Access</p>
          ) : (
            <div className="flex items-center justify-center gap-3">
              {codeValid && (
                <span className="text-sm line-through" style={{ color: "rgba(255,255,255,0.2)" }}>
                  £{overview.price.toFixed(0)}
                </span>
              )}
              <span className="text-2xl" style={{ color: "rgba(245,240,230,0.8)" }}>
                £{finalPrice.toFixed(0)}
              </span>
            </div>
          )}
        </motion.div>

        {/* Purchase button */}
        <motion.button
          variants={slideUp}
          onClick={handlePurchase}
          disabled={loading}
          className="w-full py-4 rounded-xl text-sm tracking-wide transition-all"
          style={{
            background: codeValid?.discount_percent >= 100
              ? "linear-gradient(135deg, rgba(34,85,34,0.4), rgba(34,85,34,0.2))"
              : "linear-gradient(135deg, rgba(99,102,241,0.3), rgba(99,102,241,0.15))",
            border: `1px solid ${codeValid?.discount_percent >= 100 ? "rgba(34,85,34,0.3)" : "rgba(99,102,241,0.2)"}`,
            color: "rgba(245,240,230,0.8)",
            fontFamily: "'Playfair Display', serif",
          }}
          whileTap={{ scale: 0.98 }}
          data-testid="purchase-btn"
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin mx-auto" />
          ) : codeValid?.discount_percent >= 100 ? (
            "Unlock Program"
          ) : (
            "Begin Your Journey"
          )}
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// ─── Day List (Journey Overview) ─────────────────
const JourneyOverview = ({ days, onSelectDay }) => {
  return (
    <motion.div
      className="min-h-screen p-6 pb-24"
      style={{ background: "#0a0d12" }}
      {...fadeIn}
      transition={{ duration: 0.8 }}
      data-testid="journey-overview"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 50% 10%, rgba(99,102,241,0.03) 0%, transparent 50%)",
      }} />

      <div className="relative z-10 max-w-lg mx-auto">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="text-[10px] uppercase tracking-[0.3em] mb-3"
          style={{ color: "rgba(165,180,252,0.4)" }}
        >
          The Integration Program
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="text-2xl mb-6"
          style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.85)" }}
        >
          Your Journey
        </motion.h1>

        <div className="space-y-3">
          {days.map((day, i) => {
            const hasContent = !!day.video_url || !!day.has_feature_link;
            return (
              <motion.button
                key={day.day_number}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => onSelectDay(day)}
                className="w-full text-left rounded-xl transition-all"
                style={{
                  background: hasContent
                    ? "linear-gradient(145deg, rgba(99,102,241,0.06) 0%, rgba(10,13,18,0.9) 100%)"
                    : "rgba(255,255,255,0.02)",
                  border: `1px solid ${hasContent ? "rgba(99,102,241,0.12)" : "rgba(255,255,255,0.04)"}`,
                }}
                whileTap={hasContent ? { scale: 0.98 } : {}}
                data-testid={`day-${day.day_number}`}
              >
                <div className="p-4 flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-xs font-mono flex-shrink-0"
                    style={{
                      background: hasContent ? "rgba(99,102,241,0.12)" : "rgba(255,255,255,0.04)",
                      color: hasContent ? "rgba(165,180,252,0.7)" : "rgba(255,255,255,0.15)",
                    }}
                  >
                    {day.day_number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate" style={{ color: hasContent ? "rgba(245,240,230,0.8)" : "rgba(255,255,255,0.3)", fontFamily: "'Playfair Display', serif" }}>
                      {day.title}
                    </p>
                    <p className="text-[11px] truncate" style={{ fontFamily: "'Spectral', serif", fontStyle: "italic", color: "rgba(255,255,255,0.2)" }}>
                      {day.subtitle}
                    </p>
                  </div>
                  {hasContent ? (
                    <ChevronRight size={16} style={{ color: "rgba(165,180,252,0.3)" }} />
                  ) : (
                    <Lock size={14} style={{ color: "rgba(255,255,255,0.1)" }} />
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Day Experience ──────────────────────────────
const DayExperience = ({ day, onBack, onNightIntegration }) => {
  const videoRef = useRef(null);
  const [step, setStep] = useState("branded-intro"); // branded-intro, video, reflection, complete
  const [reflection, setReflection] = useState("");
  const [saved, setSaved] = useState(false);
  const [currentVideoIdx, setCurrentVideoIdx] = useState(0); // 0 = video_url, 1 = video_2_url

  const token = localStorage.getItem("reclaim_token");

  // Auto-transition from branded intro to video
  useEffect(() => {
    if (step === "branded-intro") {
      const timer = setTimeout(() => setStep("video"), 3500);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const saveReflection = async () => {
    if (!reflection.trim()) return;
    try {
      setSaved(true);
      setTimeout(() => setStep("complete"), 1000);
    } catch {
      setStep("complete");
    }
  };

  return (
    <motion.div
      className="min-h-screen flex flex-col"
      style={{ background: "#0a0d12" }}
      {...fadeIn}
      transition={{ duration: 0.8 }}
      data-testid="day-experience"
    >
      {/* Header */}
      <div className="p-6 flex items-center gap-4 relative z-10">
        <button onClick={onBack} style={{ color: "rgba(245,240,230,0.3)" }} data-testid="day-back-btn">
          <ArrowLeft size={22} />
        </button>
        {step !== "branded-intro" && (
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em]" style={{ color: "rgba(165,180,252,0.4)" }}>
              Day {day.day_number}
            </p>
            <p className="text-sm" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.7)" }}>
              {day.title}
            </p>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {/* BRANDED INTRO */}
        {step === "branded-intro" && (
          <motion.div
            key="branded-intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="flex-1 flex flex-col items-center justify-center px-8"
            data-testid="branded-intro"
          >
            {/* Subtle atmosphere */}
            <motion.div
              className="absolute w-[300px] h-[200px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(ellipse, rgba(99,102,241,0.04) 0%, transparent 60%)", filter: "blur(60px)" }}
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 5, repeat: 999, repeatType: "loop" }}
            />
            
            <motion.div className="text-center relative z-10">
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 1 }}
                className="text-[11px] uppercase tracking-[0.5em] mb-6"
                style={{ color: "rgba(165,180,252,0.4)" }}
              >
                Reclaim
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 1 }}
                className="text-[10px] uppercase tracking-[0.3em] mb-10"
                style={{ color: "rgba(165,180,252,0.2)" }}
              >
                The Integration Program
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 1 }}
              >
                <p className="text-lg italic" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.6)" }}>
                  Day {day.day_number}
                </p>
                <p className="text-sm italic mt-2" style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.25)" }}>
                  {day.title}
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        )}

        {/* VIDEO STEP */}
        {step === "video" && (
          <motion.div key="video" className="flex-1 flex flex-col px-6 pb-8" {...fadeIn}>
            {day.video_url && (() => {
              const videos = [day.video_url, day.video_2_url].filter(Boolean);
              const currentSrc = videos[currentVideoIdx] || videos[0];
              const hasNext = currentVideoIdx < videos.length - 1;
              return (
                <>
                  <div className="rounded-2xl overflow-hidden mb-3" style={{ background: "#000", aspectRatio: "16/9" }}>
                    <video
                      ref={videoRef}
                      key={currentSrc}
                      src={`${API_URL}${currentSrc}`}
                      controls
                      playsInline
                      autoPlay={currentVideoIdx > 0}
                      className="w-full h-full"
                      style={{ objectFit: "cover" }}
                      onEnded={() => {
                        if (hasNext) {
                          setCurrentVideoIdx((i) => i + 1);
                        }
                      }}
                      data-testid={`day-video-${currentVideoIdx + 1}`}
                    />
                  </div>

                  {/* Part indicator (only if there are 2 videos) */}
                  {videos.length > 1 && (
                    <div className="flex items-center justify-center gap-2 mb-6">
                      {videos.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentVideoIdx(i)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all text-[10px] uppercase tracking-[0.2em] font-light"
                          style={{
                            background:
                              currentVideoIdx === i
                                ? "rgba(99,102,241,0.14)"
                                : "rgba(255,255,255,0.02)",
                            border: `1px solid ${
                              currentVideoIdx === i
                                ? "rgba(165,180,252,0.35)"
                                : "rgba(255,255,255,0.06)"
                            }`,
                            color:
                              currentVideoIdx === i
                                ? "rgba(165,180,252,0.85)"
                                : "rgba(245,240,230,0.35)",
                          }}
                          data-testid={`video-part-${i + 1}`}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{
                              background:
                                currentVideoIdx === i
                                  ? "rgba(165,180,252,0.85)"
                                  : "rgba(245,240,230,0.3)",
                            }}
                          />
                          Part {i + 1}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Feature link for days without video */}
            {day.has_feature_link && day.feature_link && (
              <button
                onClick={() => window.location.href = day.feature_link}
                className="w-full py-4 rounded-xl text-sm mb-6 transition-all"
                style={{
                  background: "rgba(99,102,241,0.08)",
                  border: "1px solid rgba(99,102,241,0.15)",
                  color: "rgba(165,180,252,0.6)",
                  fontFamily: "'Playfair Display', serif",
                }}
                data-testid="feature-link-btn"
              >
                {day.feature_link_label || "Open Feature"}
              </button>
            )}

            {/* Description */}
            <p className="text-sm leading-relaxed mb-8" style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.4)" }}>
              {day.description}
            </p>

            {/* Continue to Reflection */}
            <button
              onClick={() => setStep("reflection")}
              className="mt-auto w-full py-3.5 rounded-xl text-sm transition-all"
              style={{
                background: "rgba(99,102,241,0.1)",
                border: "1px solid rgba(99,102,241,0.15)",
                color: "rgba(165,180,252,0.6)",
                fontFamily: "'Playfair Display', serif",
              }}
              data-testid="continue-to-reflection"
            >
              Continue to Reflection
            </button>
          </motion.div>
        )}

        {/* REFLECTION STEP */}
        {step === "reflection" && (
          <motion.div key="reflection" className="flex-1 flex flex-col px-6 pb-8" {...fadeIn}>
            <style>{`
              textarea[data-testid="reflection-input"]::placeholder {
                color: rgba(245,240,230,0.25) !important;
                -webkit-text-fill-color: rgba(245,240,230,0.25) !important;
              }
            `}</style>
            <div className="flex items-center gap-2 mb-6">
              <BookOpen size={16} style={{ color: "rgba(165,180,252,0.4)" }} />
              <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "rgba(165,180,252,0.4)" }}>Reflection</p>
            </div>

            <p className="text-base italic mb-8" style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.5)" }}>
              {day.reflection_prompt}
            </p>

            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Write your reflection..."
              rows={6}
              className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none resize-none mb-6"
              style={{
                fontFamily: "'Spectral', serif",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(245,240,230,0.9)",
                WebkitTextFillColor: "rgba(245,240,230,0.9)",
                caretColor: "rgba(165,180,252,0.8)",
              }}
              data-testid="reflection-input"
            />

            <div className="mt-auto space-y-3">
              <button
                onClick={saveReflection}
                className="w-full py-3.5 rounded-xl text-sm transition-all"
                style={{
                  background: reflection.trim() ? "rgba(99,102,241,0.15)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${reflection.trim() ? "rgba(99,102,241,0.2)" : "rgba(255,255,255,0.05)"}`,
                  color: reflection.trim() ? "rgba(165,180,252,0.7)" : "rgba(255,255,255,0.2)",
                  fontFamily: "'Playfair Display', serif",
                }}
                data-testid="save-reflection"
              >
                {saved ? "Saved" : "Save & Continue"}
              </button>
              <button
                onClick={() => setStep("complete")}
                className="w-full py-2 text-xs"
                style={{ color: "rgba(255,255,255,0.15)" }}
              >
                Skip
              </button>
            </div>
          </motion.div>
        )}

        {/* COMPLETE STEP */}
        {step === "complete" && (
          <motion.div key="complete" className="flex-1 flex flex-col items-center justify-center px-8" {...fadeIn}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-20 h-20 rounded-full flex items-center justify-center mb-8"
              style={{ background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.15)" }}
            >
              <Check size={28} style={{ color: "rgba(165,180,252,0.6)" }} />
            </motion.div>

            <h2 className="text-xl mb-2 text-center" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.8)" }}>
              Day {day.day_number} Complete
            </h2>
            <p className="text-sm italic text-center mb-12" style={{ fontFamily: "'Spectral', serif", color: "rgba(255,255,255,0.25)" }}>
              {day.subtitle}
            </p>

            {/* Night Integration */}
            <button
              onClick={onNightIntegration}
              className="w-full max-w-sm py-4 rounded-xl text-sm transition-all flex items-center justify-center gap-3"
              style={{
                background: "linear-gradient(135deg, rgba(30,25,50,0.8), rgba(20,15,40,0.9))",
                border: "1px solid rgba(139,92,246,0.15)",
                color: "rgba(196,181,253,0.6)",
                fontFamily: "'Playfair Display', serif",
              }}
              data-testid="night-integration-btn"
            >
              <Moon size={16} />
              Begin Night Integration
            </button>

            <button
              onClick={onBack}
              className="mt-4 py-2 text-xs"
              style={{ color: "rgba(255,255,255,0.15)" }}
            >
              Return to Journey
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Main Page ───────────────────────────────────
const IntegrationProgramPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [screen, setScreen] = useState("loading");
  const [hasAccess, setHasAccess] = useState(false);
  const [overview, setOverview] = useState(null);
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  const token = localStorage.getItem("reclaim_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    checkAccess();
  }, []);

  // Handle Stripe return
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkAccess = async () => {
    try {
      // Check access
      const accessRes = await fetch(`${API_URL}/api/integration-program/access`, { headers });
      if (accessRes.ok) {
        const accessData = await accessRes.json();
        if (accessData.has_access) {
          setHasAccess(true);
          loadDays();
          setScreen("journey");
          return;
        }
      }

      // Load overview for purchase gate
      const overviewRes = await fetch(`${API_URL}/api/integration-program/overview`);
      const overviewData = await overviewRes.json();
      setOverview(overviewData);
      setScreen("purchase");
    } catch {
      setScreen("purchase");
    }
  };

  const loadDays = async () => {
    try {
      const res = await fetch(`${API_URL}/api/integration-program/days`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDays(data.days || []);
      }
    } catch (e) {
      console.error("Load days error:", e);
    }
  };

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt >= 5) return;
    try {
      const res = await fetch(`${API_URL}/api/integration-program/payment-status/${sessionId}`, { headers });
      const data = await res.json();
      if (data.payment_status === "paid") {
        setHasAccess(true);
        loadDays();
        setScreen("journey");
        // Clean URL
        window.history.replaceState({}, "", "/integration-program");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    }
  };

  if (screen === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0d12" }}>
        <Loader2 size={24} className="animate-spin" style={{ color: "rgba(165,180,252,0.4)" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#0a0d12" }}>
      <AnimatePresence mode="wait">
        {screen === "purchase" && overview && (
          <PurchaseGate
            key="purchase"
            overview={overview}
            onAccessGranted={() => {
              setHasAccess(true);
              loadDays();
              setScreen("journey");
            }}
          />
        )}

        {screen === "journey" && !selectedDay && (
          <JourneyOverview
            key="journey"
            days={days}
            onSelectDay={(day) => (day.video_url || day.has_feature_link) && setSelectedDay(day)}
          />
        )}

        {screen === "journey" && selectedDay && (
          <DayExperience
            key={`day-${selectedDay.day_number}`}
            day={selectedDay}
            onBack={() => setSelectedDay(null)}
            onNightIntegration={() => navigate(`/integration/night/${selectedDay.day_number}`)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default IntegrationProgramPage;
